import { LottieModelSettings, StateMachineConfig } from './types';

// Default state machine for Teacher Hoa
// Note: States will be auto-populated from JSON parsing
export const teacherHoaStateMachine: StateMachineConfig = {
    initialState: 'stand still and look',
    states: {
        'teaching-1': {
            name: 'teaching-2',
            startFrame: 0,
            endFrame: 360,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        angry: {
            name: 'angry',
            startFrame: 360,
            endFrame: 435,
            loop: true, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'teaching-2': {
            name: 'teaching-1',
            startFrame: 435,
            endFrame: 800,
            loop: true, // Play once then return to idle
            nextState: 'stand still and look',
        },
        hello: {
            name: 'hello',
            startFrame: 800,
            endFrame: 937,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'lean over and point': {
            name: 'lean over and point',
            startFrame: 907,
            endFrame: 1038,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'raise your right hand': {
            name: 'raise your right hand',
            startFrame: 1038,
            endFrame: 1158,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'raise your left hand': {
            name: 'raise your left hand',
            startFrame: 1158,
            endFrame: 1315,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'clap and say': {
            name: 'clap and say',
            startFrame: 1315,
            endFrame: 1460,
            loop: false, // Play once then return to idle
            nextState: 'stand still and look',
        },
        'stand still and look': {
            name: 'stand still and look',
            startFrame: 1460,
            endFrame: 1650,
            loop: true, // Idle animation should loop
        }
    },
    transitions: [
        { from: 'clap and say', to: 'stand still and look', duration: 300 },
        { from: 'stand still and look', to: 'clap and say', duration: 300 },
        { from: 'raise your left hand', to: 'stand still and look', duration: 300 },
        { from: 'stand still and look', to: 'raise your left hand', duration: 300 },
        { from: 'clap and say', to: 'raise your left hand', duration: 300 },
        { from: 'raise your left hand', to: 'clap and say', duration: 300 },
        { from: 'hello', to: 'stand still and look', duration: 300 },
        { from: 'stand still and look', to: 'hello', duration: 100 },
    ],
    mouthStates: {
        'lean over and point': 'smiling',
        'raise your right hand': 'smiling',
        'stand still and look': 'smiling',
        'clap and say': 'open_smiling',
        'raise your left hand': 'smiling',
        hello: 'open_smiling',
        'teaching-1': 'said',
        'teaching-2': 'said',
        angry: 'sad',
    },
    mouthConfigs: {
        smiling: {
            type: 'smiling' as const,
            url: '/assets/models/teacher-hoa/mouth_smilling.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: true, // Sync frames from current running state
        },
        said: {
            type: 'said' as const,
            url: '/assets/models/teacher-hoa/mouth_said.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: true,
        },
        sad: {
            type: 'sad' as const,
            url: '/assets/models/teacher-hoa/mouth_sad.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: true, // Sync frames from current running state
        },
        open_smiling: {
            type: 'open_smiling' as const,
            url: '/assets/models/teacher-hoa/mouth_open_smiling.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: true, // Sync frames from current running state
        },
        neutral: {
            type: 'neutral' as const,
            url: '/assets/models/teacher-hoa/mouth_said.json', // Using 'said' as neutral base
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: true, // Sync from specific 'stand still and look' state
        },
        // Phoneme-specific mouths for lip sync
        open_A_E_I: {
            type: 'open_A_E_I' as const,
            url: '/assets/models/teacher-hoa/mouth_open_A_E_I.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: false, // Dynamic lip sync control
            phonemes: ['A', 'E', 'I'], // Associated phonemes
        },
        open_B_M_P: {
            type: 'open_B_M_P' as const,
            url: '/assets/models/teacher-hoa/mouth_open_B_M_P.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: false, // Dynamic lip sync control
            phonemes: ['B', 'M', 'P'], // Associated phonemes
        },
        open_O: {
            type: 'open_O' as const,
            url: '/assets/models/teacher-hoa/mouth_open_O.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: false, // Dynamic lip sync control
            phonemes: ['O', 'U'], // Associated phonemes
        },
        open_EE: {
            type: 'open_EE' as const,
            url: '/assets/models/teacher-hoa/mouth_open_EE.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: false, // Dynamic lip sync control
            phonemes: ['EE'], // Associated phonemes
        },
        open_L: {
            type: 'open_L' as const,
            url: '/assets/models/teacher-hoa/mouth_open_L.json',
            framesPerSecond: 30,
            totalFrames: 30,
            syncFrameFromState: false, // Dynamic lip sync control
            phonemes: ['L', 'T', 'D'], // Associated phonemes
        },
    },
};

export const models: Record<string, LottieModelSettings> = {
    teacher_hoa: {
        url: `/assets/models/teacher-hoa/teacher_hoa_no_mouth.json`,
        name: 'Teacher Hoa',
        style: {
            width: '400%',
            height: '100%',
        },
        mainMouthForLipSync: 'said', // Main mouth animation for lip sync
        stateMachineConfig: teacherHoaStateMachine,
        lipSyncConfig: {
            defaultFramesPerPhoneme: 3,
            phonemeToFrame: {
                // Map phonemes to frame indices in the 'said' mouth animation
                A: 5,
                E: 3,
                I: 2,
                O: 7,
                U: 8,
                M: 0,
                B: 1,
                P: 1,
                F: 4,
                V: 4,
                T: 2,
                D: 2,
                S: 3,
                Z: 3,
                silence: 0,
            },
        },
        voice: 'alloy',
    },
};
